import { useRef, useState } from "react";
import { UploadCloud, Loader2, CheckCircle2, X, Link2, RefreshCw } from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

// Direct 9:16 upload to Cloudflare Stream. The API token stays server-side;
// the browser only receives a one-time upload URL.
export function CloudflareVideo({ value, onChange }) {
  const inputRef = useRef(null);
  const [progress, setProgress] = useState(null); // null | 0-100 | 'processing'
  const [status, setStatus] = useState(null); // {ready, status}
  const [manual, setManual] = useState(false);

  const pollReady = async (uid) => {
    setProgress("processing");
    for (let i = 0; i < 60; i++) {
      try {
        const { data } = await api.get(`/admin/cloudflare/video/${uid}`);
        setStatus(data);
        if (data.ready) { setProgress(null); toast.success("Video ready to stream"); return; }
      } catch (e) {}
      await new Promise((r) => setTimeout(r, 3000));
    }
    setProgress(null);
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setProgress(0);
      const { data } = await api.post("/admin/cloudflare/direct-upload", { name: file.name });
      const { upload_url, uid } = data;
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", upload_url, true);
        xhr.upload.onprogress = (ev) => { if (ev.lengthComputable) setProgress(Math.round((ev.loaded / ev.total) * 100)); };
        xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error("Upload failed")));
        xhr.onerror = () => reject(new Error("Upload failed"));
        const fd = new FormData();
        fd.append("file", file);
        xhr.send(fd);
      });
      onChange(uid);
      toast.success("Uploaded — processing video…");
      pollReady(uid);
    } catch (err) {
      setProgress(null);
      toast.error(err.response?.data?.detail || "Cloudflare upload failed");
    }
  };

  const checkStatus = async () => {
    if (!value) return;
    try { const { data } = await api.get(`/admin/cloudflare/video/${value}`); setStatus(data); }
    catch (e) { toast.error("Could not fetch video status"); }
  };

  return (
    <div data-testid="cloudflare-video" className="rounded-lg border border-[#DCA846]/30 bg-[#DCA846]/5 p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-[#DCA846]">Cloudflare Stream Video (9:16, secure)</span>
        <button type="button" onClick={() => setManual((m) => !m)} className="text-[10px] text-zinc-400 hover:text-white">
          <Link2 className="mr-1 inline h-3 w-3" />{manual ? "Hide" : "Paste UID"}
        </button>
      </div>

      {value ? (
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 rounded bg-black/40 px-3 py-2 text-xs text-white">
            {progress === "processing" ? <Loader2 className="h-4 w-4 animate-spin text-[#DCA846]" /> :
              status?.ready === false ? <Loader2 className="h-4 w-4 animate-spin text-yellow-400" /> :
                <CheckCircle2 className="h-4 w-4 text-emerald-400" />}
            <span className="font-mono max-w-[180px] truncate">{value}</span>
          </div>
          <button type="button" onClick={checkStatus} title="Refresh status" className="text-zinc-400 hover:text-white"><RefreshCw className="h-4 w-4" /></button>
          <button type="button" onClick={() => inputRef.current?.click()} className="text-xs text-[#DCA846]">Replace</button>
          <button type="button" onClick={() => { onChange(null); setStatus(null); }} className="text-red-400"><X className="h-4 w-4" /></button>
        </div>
      ) : (
        <button type="button" onClick={() => inputRef.current?.click()} disabled={progress !== null}
          data-testid="cloudflare-upload-btn"
          className="flex w-full items-center justify-center gap-2 rounded border border-dashed border-[#DCA846]/40 bg-black/30 py-5 text-sm text-zinc-300 hover:border-[#DCA846] transition-colors">
          {progress === null ? <><UploadCloud className="h-5 w-5 text-[#DCA846]" /> Upload 9:16 video to Cloudflare</> :
            progress === "processing" ? <><Loader2 className="h-5 w-5 animate-spin text-[#DCA846]" /> Processing…</> :
              <><Loader2 className="h-5 w-5 animate-spin text-[#DCA846]" /> Uploading {progress}%</>}
        </button>
      )}

      {progress !== null && progress !== "processing" && typeof progress === "number" && (
        <div className="mt-2 h-1.5 w-full overflow-hidden rounded bg-zinc-800">
          <div className="h-full bg-[#DCA846] transition-all" style={{ width: `${progress}%` }} />
        </div>
      )}

      {manual && (
        <Input placeholder="Paste Cloudflare video UID or URL" defaultValue={value || ""}
          onBlur={(e) => onChange(e.target.value.trim() || null)}
          className="mt-2 bg-zinc-900 border-white/10 text-white text-xs font-mono" />
      )}

      <input ref={inputRef} type="file" accept="video/*" onChange={handleFile} className="hidden" />
      <p className="mt-2 text-[10px] text-zinc-500">Paid lessons play via short-lived signed tokens — the video URL can't be shared. Free-preview lessons play publicly.</p>
    </div>
  );
}
